import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import MetaFormFields from 'Views/Onboarding/Pages/Survey/SurveyStep/Components/MetaFormFields';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import PageModalTitle from 'Views/Page/Content/Modal/Components/PageModalTitle';
import WhatIfStepNavigator from 'Views/Page/Content/Modal/Components/WhatIfStepNavigator';
import Typography from 'Components/UI/Typography';
import Chip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Chip';
import ListItem2 from 'Components/ComponentLibrary/Components/ListItems/ListItem2';
import List from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/List';
import ListItemText1 from 'Components/ComponentLibrary/Components/Uncategorized/ListItemText/ListItemText1';
import { currencyFromCents } from 'Helpers/Formats';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  fields: {
    padding: theme.spacing(0),
    width: '100%',
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  chip: {
    marginRight: theme.spacing(2),
  },
  list: {
    width: '100%',
  },
  outcomeAssociationsSummary: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    borderRadius: 18,
    width: '100%',
    backgroundColor: theme.palette.facet.lightGray5,
  },
  outcomeAssociation: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  inlineValue: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  textPrompt: {
    marginRight: theme.spacing(1),
  },
});

const FacetPlanVirtualAccountFundingInstructionsAccountDetailsStep = ({
  className,
  classes,
}) => {
  const { saving, resource, onSubmit } = useMetaLayoutContext();
  const { loadModalCollectionByLink } = useModalContext();

  const previousLink = resource.relationship(fr.relationships.previousLink.name)?.link('related');

  const outcomeAssociations = resource.attr('outcomeAssociations')?.resources().list() || [];
  const accountFundingInstructions = resource.attr('accountFundingInstructions')?.resources().list() || [];

  return (
    <Box
      className={classNames(classes.root, className)}
    >
      <PageModalTitle>
        <Title text="Funding Instructions" />
      </PageModalTitle>

      <div className={classes.outcomeAssociationsSummary}>
        <Typography className={classes.header} color="primaryGray">
          {'Investment Objective(s) & Time Horizon(s)'}
        </Typography>

        {outcomeAssociations.map(el => (
          <div className={classes.outcomeAssociation}>
            <Chip color="primary" label={el.attr('outcomeName')} className={classes.chip} />
            <Typography color="primaryGray">{el.attr('yearsToEnd')}</Typography>
          </div>
        ))}
      </div>

      <List className={classes.list}>
        {accountFundingInstructions.map(el => (
          <ListItem2
            color={'lightGray5'}
            // button
            // onClick={() => {}}
          >
            <ListItemText1
              disableTypography
              primary={(
                <Typography color="primary" variant="h5" weight="bold">
                  {el.attr('title')}
                </Typography>
              )}
              secondary={(
                <div>
                  { el.attr('sourceAccountName')
                    ? (
                      <div className={classes.inlineValue}>
                        <Typography color="primary" className={classes.textPrompt}>{'Source Account Name:'}</Typography>
                        <Typography color="primaryGray">{el.attr('sourceAccountName')}</Typography>
                      </div>
                    ) : null }
                  { el.attr('amount')
                    ? (
                      <div className={classes.inlineValue}>
                        <Typography color="primary" className={classes.textPrompt}>{'Amount:'}</Typography>
                        <Typography color="primaryGray">{currencyFromCents(el.attr('amount'), false)}</Typography>
                      </div>
                    ) : null }
                  { el.attr('frequency')
                    ? (
                      <div className={classes.inlineValue}>
                        <Typography color="primary" className={classes.textPrompt}>{'Frequency:'}</Typography>
                        <Typography color="primaryGray">{el.attr('frequency')}</Typography>
                      </div>
                    ) : null }
                  { el.attr('transferDate')
                    ? (
                      <div className={classes.inlineValue}>
                        <Typography color="primary" className={classes.textPrompt}>{'Transfer Date:'}</Typography>
                        <Typography color="primaryGray">{el.attr('transferDate')}</Typography>
                      </div>
                    ) : null }
                  <Typography color="primary">{'Additional Instructions:'}</Typography>
                  <Typography color="primaryGray">{el.attr('additionalInstruction')}</Typography>
                </div>
              )}
            />
          </ListItem2>
        ))}
      </List>

      <MetaFormFields
        className={classes.fields}
        resource={resource}
      />
      {resource.attr('accountFundingInstructions')?.meta().action('PUT')
        ? (
          <WhatIfStepNavigator
            isLoading={saving}
            onNext={() => {
              onSubmit();
            }}
            onPrevious={(previousLink) ? () => {
              loadModalCollectionByLink(previousLink.url());
            } : null}
          />
        ) : null
      }
    </Box>
  );
};

FacetPlanVirtualAccountFundingInstructionsAccountDetailsStep.defaultProps = {
  className: '',
};

FacetPlanVirtualAccountFundingInstructionsAccountDetailsStep.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(FacetPlanVirtualAccountFundingInstructionsAccountDetailsStep);
